import { el } from "../../utils/dom";

interface Shortcut {
    keys: string[];
    description: string;
}

const shortcuts: Shortcut[] = [
    { keys: ["Ctrl/Cmd", "Z"], description: "Undo last action" },
    { keys: ["Ctrl/Cmd", "Y"], description: "Redo action" },
    { keys: ["Ctrl/Cmd", "Shift", "Z"], description: "Redo action (alternative)" },
    { keys: ["?"], description: "Show keyboard shortcuts" },
    { keys: ["Esc"], description: "Close dialogs" },
];

/**
 * Open the keyboard shortcuts help panel
 */
export function openShortcutsPanel(): void {
    // Create modal backdrop
    const backdrop = el("div", {
        style: `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.5);
            z-index: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
        `
    });

    // Create dialog
    const dialog = el("div", {
        style: `
            background: var(--card);
            border-radius: 16px;
            padding: 24px;
            max-width: 450px;
            width: 90%;
            border: 1px solid var(--border);
            box-shadow: 0 4px 24px rgba(0,0,0,0.2);
        `
    });

    const title = el("h2", {
        style: "margin: 0 0 20px 0; display: flex; align-items: center; gap: 8px;"
    });
    title.appendChild(el("span", {}, "⌨️"));
    title.appendChild(el("span", {}, "Keyboard Shortcuts"));

    const list = el("div", { style: "display: flex; flex-direction: column; gap: 12px;" });

    for (const shortcut of shortcuts) {
        const row = el("div", {
            style: "display: flex; justify-content: space-between; align-items: center;"
        });

        const keysContainer = el("div", { style: "display: flex; gap: 4px;" });
        for (let i = 0; i < shortcut.keys.length; i++) {
            const key = shortcut.keys[i];
            if (!key) continue;

            const keyEl = el("kbd", {
                style: `
                    display: inline-block;
                    padding: 4px 8px;
                    background: var(--bg);
                    border: 1px solid var(--border);
                    border-radius: 4px;
                    font-family: monospace;
                    font-size: 12px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                `
            }, key);
            keysContainer.appendChild(keyEl);

            // Add "+" between keys
            if (i < shortcut.keys.length - 1) {
                keysContainer.appendChild(el("span", {
                    style: "color: var(--muted); margin: 0 2px;"
                }, "+"));
            }
        }

        const description = el("span", {
            style: "color: var(--muted); font-size: 14px;"
        }, shortcut.description);

        row.append(keysContainer, description);
        list.appendChild(row);
    }

    // Close button
    const closeBtn = el("button", {
        class: "primary",
        style: "width: 100%; margin-top: 20px;"
    }, "Close");

    const close = () => {
        backdrop.remove();
    };

    closeBtn.addEventListener("click", close);

    // Close on backdrop click
    backdrop.addEventListener("click", (e) => {
        if (e.target === backdrop) close();
    });

    // Close on Escape key
    const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            close();
            document.removeEventListener("keydown", handleKeydown);
        }
    };
    document.addEventListener("keydown", handleKeydown);

    dialog.append(title, list, closeBtn);
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);
}

/**
 * Setup global keyboard shortcut for opening the panel
 */
export function setupShortcutsPanelKeyboard(): void {
    document.addEventListener("keydown", (e) => {
        // Ignore if user is typing in input/textarea
        const target = e.target as HTMLElement;
        if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
            return;
        }

        // Open shortcuts panel on "?" key
        if (e.key === "?" || (e.shiftKey && e.key === "/")) {
            e.preventDefault();
            openShortcutsPanel();
        }
    });
}
